/**
 * The dashboard's headline tiles (SF-5095): customer companies active in the month, split by
 * sales channel, and the month's feedback. SF-5117 adds a per-day figure and a trend line under
 * each feedback tile, and the new and gone quiet counts under active companies. A field an older
 * API leaves out leaves its line out. SF-5124: the Companies using Eva tile, tiles that wrap
 * instead of squashing (companies on one row, feedback on the next at lg), and the empty-month
 * message in place of a zero tile's lines.
 */
import { Alert, Button, Col, Row, Typography } from 'antd';
import { MessageOutlined, RobotOutlined, TeamOutlined } from '@ant-design/icons';
import { KpiTile } from '../../../components/kpi/KpiTile';
import { TrendText } from '../../../components/usage/TrendText';
import type { DailyAverage, PlatformUsage } from '../../../components/usage/usageClient';
import { surveysTotal } from '../../../components/usage/usageNumbers';
import {
  comparisonCaption,
  dailyValue,
  EMPTY_MONTH_TEXT,
  isEmptyMonth,
  isInProgress,
  perDayText,
} from '../../../components/usage/usageProgress';
import { formatNumber } from '../../../utils/format';
import { SALES_CHANNEL_LABELS } from '../../companies/constants';
import { NewGone, TileLines } from './TileLines';

export interface PlatformTilesProps {
  data: PlatformUsage | undefined;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  /** Takes staff to the Changes section (from the new or gone quiet count). */
  onShowChanges?: () => void;
}

const { Text } = Typography;

const ACTIVE_HINT = 'Paid and Free customers with any feedback in the chosen month';
const EVA_HINT = 'Customers that used Eva in the chosen month, as in the Eva row of Feature usage';
/** Tiles fill their column's height, so a row of tiles lines up. */
const tileStyle: React.CSSProperties = { height: '100%' };

export const PlatformTiles: React.FC<PlatformTilesProps> = ({
  data,
  loading,
  error,
  onRetry,
  onShowChanges,
}) => {
  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Couldn't load the platform figures"
        action={
          <Button size="small" onClick={onRetry}>
            Retry
          </Button>
        }
      />
    );
  }
  const active = data?.active_companies;
  const eva = data?.eva;
  // The Eva tile shows while loading too, so the tiles do not jump once an API with Eva answers.
  const showEva = loading || eva !== undefined;
  const companyCol = { xs: 24, sm: 12, lg: showEva ? 6 : 8 };
  const feedbackCol = { xs: 24, sm: 12, lg: 8 };
  const emptyMonth = isEmptyMonth(data?.progress, data?.totals);
  const emptyLine = <Text type="secondary">{EMPTY_MONTH_TEXT}</Text>;
  /** A tile's lines, or the empty-month message when the month has nothing counted yet. */
  const linesOr = (value: number | undefined, lines: React.ReactNode) =>
    emptyMonth && !value ? <TileLines lines={[emptyLine]} /> : lines;
  // On the 1st no full day is counted yet, so there is nothing to compare.
  const noDays = isInProgress(data?.progress) && data?.progress?.days_counted === 0;
  const caption = comparisonCaption(data?.progress);
  const trend = (current: number | undefined, previous: number | undefined) =>
    noDays || previous === undefined ? null : (
      <TrendText current={current} previous={previous} caption={caption} />
    );
  const feedbackLines = (
    current: number | undefined,
    previous: number | undefined,
    field: keyof DailyAverage,
  ) =>
    linesOr(
      current,
      <TileLines
        lines={[perDayText(dailyValue(data?.daily_average, field)), trend(current, previous)]}
      />,
    );
  const totals = data?.totals;
  const prev = data?.previous_totals;
  const surveys = totals && surveysTotal(totals);
  return (
    <Row gutter={[16, 16]} align="stretch">
      <Col {...companyCol}>
        <KpiTile
          title="Active companies"
          value={formatNumber(active?.total)}
          icon={<TeamOutlined />}
          loading={loading}
          hint={ACTIVE_HINT}
          style={tileStyle}
          footer={linesOr(
            active?.total,
            <TileLines
              lines={[
                data && <NewGone data={data} onShowChanges={onShowChanges} />,
                trend(active?.total, active?.previous_total),
              ]}
            />,
          )}
        />
      </Col>
      {(['self_serve', 'enterprise'] as const).map((channel) => (
        <Col {...companyCol} key={channel}>
          <KpiTile
            title={SALES_CHANNEL_LABELS[channel]}
            value={formatNumber(active?.[channel])}
            loading={loading}
            hint={ACTIVE_HINT}
            style={tileStyle}
          />
        </Col>
      ))}
      {showEva && (
        <Col {...companyCol}>
          <KpiTile
            title="Companies using Eva"
            value={formatNumber(eva?.companies_using)}
            icon={<RobotOutlined />}
            loading={loading}
            hint={EVA_HINT}
            style={tileStyle}
            footer={linesOr(
              eva?.companies_using,
              <TileLines lines={[trend(eva?.companies_using, eva?.previous_companies_using)]} />,
            )}
          />
        </Col>
      )}
      <Col {...feedbackCol}>
        <KpiTile
          title="Total feedback"
          value={formatNumber(totals?.total_feedback)}
          icon={<MessageOutlined />}
          loading={loading}
          style={tileStyle}
          footer={feedbackLines(totals?.total_feedback, prev?.total_feedback, 'total_feedback')}
          hint="Surveys and old style ratings from customers. Demo, Test and fake data are left out."
        />
      </Col>
      <Col {...feedbackCol}>
        <KpiTile
          title="Surveys"
          value={formatNumber(surveys)}
          loading={loading}
          style={tileStyle}
          footer={feedbackLines(surveys, prev && surveysTotal(prev), 'surveys')}
        />
      </Col>
      <Col {...feedbackCol}>
        <KpiTile
          title="Old style ratings"
          value={formatNumber(totals?.ratings)}
          loading={loading}
          style={tileStyle}
          footer={feedbackLines(totals?.ratings, prev?.ratings, 'ratings')}
        />
      </Col>
    </Row>
  );
};
